import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { distinctUntilChanged, skip, tap } from 'rxjs/operators';
import { merge } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../core/reducers';
import { LayoutUtilsService, QueryParamsModel } from '../../../../../../core/_base/crud';
import {
  PatientsImageChangeRequestsDataSource,
  PatientsImageChangeRequestsPageRequested,
  DenyPatientImageChangeRequest,
  ApprovePatientImageChangeRequest,
  selectPatientImageChangeRequestLatestSuccessfullAction,
} from '../../../../../../core/lists';
import { MY_FORMATS } from '../patient-edit/patient-edit.component';
import { StringUtilsService } from '../../../../../../core/services/string-utils.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import { NavigationService } from '../../../../../../core/services/navigation.service';
import { ConfirmDenialComponent } from './confirm-denial-dialog/confirm-denial-dialog.component';
import { PatientImageChangeRequest } from 'src/app/core/lists/_models/patient-image-change-request.model';
import { PatientRequestReviewComponent } from '../patient-request-review/patient-request-review.component';

@Component({
  selector: 'kt-patients-requests',
  templateUrl: './patients-requests.component.html',
  styleUrls: ['./patients-requests.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_FORMATS,
    },
  ],
})
export class PatientsRequestsComponent implements OnInit, OnDestroy {
  dataSource: PatientsImageChangeRequestsDataSource;
  displayedColumns = ['patientId', 'patientLastName', 'status', 'created', 'actions'];
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild('sort', { static: true }) sort: MatSort;
  filteredStatus = 'All';
  private statuses = ['Approved', 'Denied', 'Canceled', 'Pending'];
  statusTracker = -1;
  private subscriptions: any[] = [];
  @ViewChild('fileInput', { static: true }) fileInput: ElementRef;
  filtersApplied: any = false;
  firstLoadAfterBack: any = false;
  pageSize;
  exporting = false;
  constructor(
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    private layoutUtilsService: LayoutUtilsService,
    private translate: TranslateService,
    private store: Store<AppState>,
    public stringUtils: StringUtilsService,
    private navigationService: NavigationService,
  ) {}

  ngOnInit(): void {
    this.filtersApplied = this.navigationService.getFilters();

    const sortSubscription = this.sort.sortChange.subscribe(() => {
      this.paginator.pageIndex = this.paginator?.pageIndex || 0;
      this.sort.active = this.sort.direction ? this.sort.active : 'requesterId';
      this.sort.direction = this.sort.direction || 'desc';
    });

    this.subscriptions.push({ id: 1, sub: sortSubscription });

    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadPatientsImageChangeRequestsList()))
      .subscribe();

    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    this.dataSource = new PatientsImageChangeRequestsDataSource(this.store);

    const entitiesSubscription = this.dataSource.entitySubject
      .pipe(skip(1), distinctUntilChanged())
      .subscribe();

    this.subscriptions.push({ id: 4, sub: entitiesSubscription });

    const actionSub = this.store
      .pipe(select(selectPatientImageChangeRequestLatestSuccessfullAction), skip(1))
      .subscribe(() => {
        this.reloadPatientImageChangeRequestsList();
      });

    this.subscriptions.push({ id: 5, sub: actionSub });

    if (this.filtersApplied) {
      this.firstLoadAfterBack = true;
      this.pageSize = this.filtersApplied?.pageSize;
    }

    setTimeout(() => this.loadPatientsImageChangeRequestsList());
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => el.sub.unsubscribe());
  }

  loadPatientsImageChangeRequestsList() {
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction || 'desc',
      this.sort.active || 'created',
      this.paginator.pageIndex,
      this.paginator.pageSize || this.pageSize || 25,
    );
    this.store.dispatch(PatientsImageChangeRequestsPageRequested({ page: queryParams }));
  }

  filterConfiguration(reset_applied_flag?): any {
    const filter: any = {};
    filter.status = this.filteredStatus === 'All' ? '' : this.filteredStatus;

    if (this.filtersApplied && this.firstLoadAfterBack) {
      this.firstLoadAfterBack = false;
      const appliedFilters = this.filtersApplied;
      filter.status = appliedFilters.status;
      this.filteredStatus = appliedFilters.isActive;

      this.sort.direction = this.filtersApplied?.sortDirection || 'desc';
      this.sort.active = this.filtersApplied?.sortActive || 'created';
      this.paginator.pageIndex = this.filtersApplied?.pageIndex || 0;
      this.paginator.pageSize = this.filtersApplied?.pageSize || 25;
    }
    if (reset_applied_flag) this.filtersApplied = false;

    filter.sortDirection = this.sort.direction || 'desc';
    filter.sortActive = this.sort.active || 'created';
    filter.pageIndex = this.paginator.pageIndex;
    filter.pageSize = this.paginator.pageSize;
    return filter;
  }

  viewRequest(request: PatientImageChangeRequest): void {
    this.dialog
      .open(PatientRequestReviewComponent, {
        data: { request },
      })
      .afterClosed()
      .subscribe((res) => {
        if (!res) return;

        const action = res.action + 'Request';

        this[action](request);
      });
  }

  getItemCssClassByStatus(status: string): string {
    switch (status.toLowerCase()) {
      case 'approved':
        return 'success';
      case 'canceled':
        return 'dark';
      case 'denied':
        return 'danger';
      default:
        return 'warning';
    }
  }

  getItemStatusString(status: string): string {
    if (status === 'All') return 'All';

    return this.translate.instant(`PATIENT.IMAGE_REQUESTS.STATUS.${status.toUpperCase()}`);
  }

  reloadPatientImageChangeRequestsList(): void {
    this.paginator.pageIndex = 0;

    this.loadPatientsImageChangeRequestsList();
  }

  toggleStatus(event: Event): void {
    event.stopPropagation();

    const initialStatus = this.statusTracker;
    this.statusTracker = (this.statusTracker + 1) % this.statuses.length;
    let status = 'All';

    if (this.statusTracker == 0 && initialStatus != -1) {
      this.statusTracker = -1;
    } else {
      status = this.statuses[this.statusTracker];
    }

    this.filteredStatus = status;

    this.reloadPatientImageChangeRequestsList();
  }

  approveRequest(request) {
    this.layoutUtilsService
      .confirmAction(
        this.translate.instant('PATIENT.IMAGE_REQUESTS.APPROVE_REQUEST_SIMPLE.TITLE'),
        this.translate.instant('PATIENT.IMAGE_REQUESTS.APPROVE_REQUEST_SIMPLE.DESCRIPTION'),
      )
      .afterClosed()
      .subscribe((res) => {
        if (!res) return;

        this.store.dispatch(ApprovePatientImageChangeRequest({ request }));
      });
  }

  denyRequest(request) {
    this.dialog
      .open(ConfirmDenialComponent, {
        data: {
          title: 'Are you sure you want to deny this request?',
          description: 'Before proceeding, please specify the reason for the denial.',
        },
      })
      .afterClosed()
      .subscribe((res) => {
        if (!res) return;

        this.store.dispatch(DenyPatientImageChangeRequest({ request, reason: res.reason }));
      });
  }
}
